import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Leaf, Heart, Users, BookOpen, MapPin, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const SunyateeFoundation = () => (
  <div className="space-y-8">
    {/* Header */}
    <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white">
      <div className="flex items-center justify-center mb-4">
        <img 
          src="https://sifworld.com/wp-content/uploads/2024/12/optimized-fvru.webp" 
          alt="Sunyatee International Foundation"
          className="h-24 object-contain"
        />
      </div>
      <h2 className="text-3xl font-bold mb-4 text-center">Sunyatee International Foundation</h2>
      <p className="text-emerald-100 text-center max-w-3xl mx-auto">
        Founded in 2019 (under guidance since 2015), empowering lives and inspiring change through peace, prosperity, and awareness
      </p>
    </div>

    {/* Mission & Leadership */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Mission</h3>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Promote global peace and prosperity</li>
          <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Blend spiritual and socioeconomic initiatives</li>
          <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Revive ancient wisdom for modern transformation</li>
          <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Create peace ambassadors globally</li>
          <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Support marginalized communities</li>
          <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Cultivate values of equality, justice, and dignity</li>
        </ul>
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Leadership</h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-bold text-gray-900">Datuk Dr. Lim Siow Jin</h4>
            <p className="text-sm text-gray-600">Founder & Head (Dhyanacharya Nagajiva)</p>
            <p className="text-sm text-gray-600 mt-1">Guiding Principle - Timeless wisdom of Sunya</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900">Dr. Rajesh Savera</h4>
            <p className="text-sm text-gray-600">Director & Sunya Pravartak</p>
            <ul className="text-xs text-gray-600 mt-2 space-y-1">
              <li>• Ayurveda Physician (BAMS)</li>
              <li>• MS - Counselling & Psychotherapy</li>
              <li>• Life & Wellness Coach</li>
              <li>• 18+ years in "Lifestyle As Medicine"</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    {/* Core Activities */}
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Core Activities & Programs</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Sunya Retreats */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-emerald-200 hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4 mx-auto">
            <img src="https://sifworld.com/wp-content/uploads/2024/12/2.png" alt="Sunya Meditation" className="w-12 h-12 object-contain" />
          </div>
          <h4 className="font-bold text-gray-900 text-center mb-3">Sunya Meditation Retreats</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>3-day residential/non-residential programs</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Structured meditation sessions</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Progressive through 7 belt levels</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Siddipet, Nagpur, Pune centers</li>
          </ul>
        </div>

        {/* Farmer Empowerment */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-amber-200 hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4 mx-auto">
            <img src="https://sifworld.com/wp-content/uploads/2024/12/1.png" alt="Farmer Empowerment" className="w-12 h-12 object-contain" />
          </div>
          <h4 className="font-bold text-gray-900 text-center mb-3">Farmer Empowerment</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-amber-600 mr-2">•</span>Cultivating prosperity in communities</li>
            <li className="flex items-start"><span className="text-amber-600 mr-2">•</span>Sustainable farming practices</li>
            <li className="flex items-start"><span className="text-amber-600 mr-2">•</span>Empowering farming families</li>
            <li className="flex items-start"><span className="text-amber-600 mr-2">•</span>Long-term resilience programs</li>
          </ul>
        </div>

        {/* Women Empowerment */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-pink-200 hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center mb-4 mx-auto">
            <img src="https://sifworld.com/wp-content/uploads/2024/12/7.png" alt="Women Empowerment" className="w-12 h-12 object-contain" />
          </div>
          <h4 className="font-bold text-gray-900 text-center mb-3">Women Empowerment</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-pink-600 mr-2">•</span>Skills for sustainable livelihoods</li>
            <li className="flex items-start"><span className="text-pink-600 mr-2">•</span>Self-help group formation</li>
            <li className="flex items-start"><span className="text-pink-600 mr-2">•</span>Financial independence programs</li>
            <li className="flex items-start"><span className="text-pink-600 mr-2">•</span>Building confidence & respect</li>
          </ul>
        </div>

        {/* Ancient Wisdom Revival */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-purple-200 hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4 mx-auto">
            <img src="https://sifworld.com/wp-content/uploads/2024/12/6.png" alt="Ancient Culture" className="w-12 h-12 object-contain" />
          </div>
          <h4 className="font-bold text-gray-900 text-center mb-3">Ancient Wisdom Revival</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Pali Tipitaka translation project</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Sutra printing on palm leaves</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Ekottara Agama studies</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Preserving spiritual heritage</li>
          </ul>
        </div>

        {/* Relief & Rehabilitation */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200 hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto">
            <img src="https://sifworld.com/wp-content/uploads/2024/12/4.png" alt="Relief" className="w-12 h-12 object-contain" />
          </div>
          <h4 className="font-bold text-gray-900 text-center mb-3">Relief & Rehabilitation</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>COVID-19 pandemic relief</li>
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Food distribution programs</li>
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Healthcare support</li>
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Disaster response initiatives</li>
          </ul>
        </div>

        {/* Health Awareness */}
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-200 hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4 mx-auto">
            <img src="https://sifworld.com/wp-content/uploads/2024/12/9.png" alt="Health" className="w-12 h-12 object-contain" />
          </div>
          <h4 className="font-bold text-gray-900 text-center mb-3">Health Awareness Camps</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-green-600 mr-2">•</span>Addressing child malnutrition</li>
            <li className="flex items-start"><span className="text-green-600 mr-2">•</span>Underserved community support</li>
            <li className="flex items-start"><span className="text-green-600 mr-2">•</span>Special needs children programs</li>
            <li className="flex items-start"><span className="text-green-600 mr-2">•</span>Holistic wellness education</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Impact Statistics */}
    <div className="bg-gradient-to-br from-slate-900 to-emerald-900 rounded-2xl p-8 text-white">
      <h3 className="text-2xl font-bold mb-8 text-center">SIF's Impact at a Glance</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="text-center">
          <div className="text-4xl font-bold text-emerald-300 mb-2">50+</div>
          <div className="text-sm text-gray-300">Projects</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-emerald-300 mb-2">20+</div>
          <div className="text-sm text-gray-300">Meditation Retreats</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-emerald-300 mb-2">1,000+</div>
          <div className="text-sm text-gray-300">Certificates Distributed</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-emerald-300 mb-2">1,000+</div>
          <div className="text-sm text-gray-300">Volunteers</div>
        </div>
      </div>
    </div>

    {/* Authentic Practitioner Images - Blue Kimono from sunyatee.org */}
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Sunyatees in Blue Kimono - Meditation Sessions</h3>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        Authentic moments from Sunya retreats - practitioners in traditional blue kimono during meditation
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="group relative rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://lh3.googleusercontent.com/QkotA6N3bFjVbnq9AHdWDh52hXLwOavRpI0TkDe90jUEj6-wdzfCjtvc_3CnvY9qykC0dzywvIQ2LaMn8j5sp21GbChcGnRlxpmiJsTxn92aApTx5zDWJrS-rf-auGZskWBBcD9hLA=w2400"
            alt="Sunyatees in Blue Kimono - Bodhgaya"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">Bodhgaya Retreat 2019 - Blue Kimono</p>
          </div>
        </div>
        <div className="group relative rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://lh3.googleusercontent.com/Qb2OyhZ41k59aEJlIbukWml4Li6SX2n4ZlDz_6-ULNdCbML-SGBBsjc89nIjvgfBxypV7THA_8Txi6ybu5CmbyKE-TWhdwhv5baE01AK-oyXSQLB6Ntw7sFGUnLzFsYYz6vwxvSQsQ=w2400"
            alt="Blue Kimono Meditation"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">Practitioners in Blue Attire</p>
          </div>
        </div>
        <div className="group relative rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://lh3.googleusercontent.com/xQt6HJYcyfTiAEpTyPxIeR1RgwLFRAmlsQNevg6pPohwPmmDyzexlQurzS67cC_DiJEHsUdjX_KL_OYh4tm6bTF0DodpaAZfAX1EoiNB5qSEoJRJrkW0DdSEngyVGw8NEd9T7vS88A=w2400"
            alt="Group Sunya Practice"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">Group Sunya Practice</p>
          </div>
        </div>
        <div className="group relative rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://lh3.googleusercontent.com/tDhQ2M_9Qvz6U7KNeQNOnFDTqXgorMh_sj_5KUbu2y9UqX9yZOirddUYlcDSgRaDaYXVMsXgTnt7XZYX_LSTilJW5bF22bACGAFLGVG6Ajye6FZX0kfskH4m8ASE3kjvjoWSkeux8w=w2400"
            alt="Siddipet Retreat Blue Kimono"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">Siddipet Retreat 2019</p>
          </div>
        </div>
        <div className="group relative rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://lh3.googleusercontent.com/G5BnUP1GbAx6DX1_vTSLe4qVnhe4zHhd6Z2wtkxLEw915zlvI0Rkw8IT8itXnzn8LX5JQwq2H9GxssfkxQ0g_oDAYIoe7r2vwah2_RlGW7ollpVgoDl99ldVbe1LJwu7S283kuoTKg=w2400"
            alt="Peaceful Blue Kimono Meditation"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">Awareness & Transformation</p>
          </div>
        </div>
        <div className="group relative rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://lh3.googleusercontent.com/8VovjADCONXaxb4LelyrivSunk8PREuChYuMKm4DWvrdgRhcM1dwdzrYufoePZlX4oHOdMBLPssuQpQFSxHSwZILrm6NKu-_EFvlXZXHJ70Eu2_VlaiVoozxug_9WWrYLmhXTxcnfg=w2400"
            alt="Begumpet Meditation Session"
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white font-semibold text-sm">Begumpet Session - Blue Kimono</p>
          </div>
        </div>
      </div>
    </div>

    {/* Online Training */}
    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Sunya Online Training Programs</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Program Features:</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Led by Dr. Lim Siow Jin and Dr. Rajesh Savera</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Accessible digital platform</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Guided meditation sessions</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Philosophical teachings</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Ancient Sutra study</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Community Benefits:</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start"><span className="text-cyan-600 mr-2">•</span>100+ talk shows and online events</li>
            <li className="flex items-start"><span className="text-cyan-600 mr-2">•</span>Interactive discussions</li>
            <li className="flex items-start"><span className="text-cyan-600 mr-2">•</span>Global community building</li>
            <li className="flex items-start"><span className="text-cyan-600 mr-2">•</span>Progress tracking</li>
            <li className="flex items-start"><span className="text-cyan-600 mr-2">•</span>Corporate wellness programs</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Registration Info */}
    <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
      <h4 className="font-bold text-gray-900 mb-3">Organization Details</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
        <div>
          <p><strong>Registration:</strong> Section 8 Non-Government Company, India</p>
          <p><strong>DIN:</strong> 05352323 (Dr. Rajesh Savera)</p>
          <p><strong>Founded:</strong> 2019 (guided since 2015)</p>
        </div>
        <div>
          <p><strong>Registered Office:</strong></p>
          <p className="text-xs">Survey No. 392 and 206, Siddipet Industrial Park,</p>
          <p className="text-xs">Mandhapally Village, Siddipet, Telangana, India</p>
        </div>
      </div>
    </div>
  </div>
);

const SunyaWhatIs = () => (
  <div className="space-y-8">
    {/* Main Introduction */}
    <div className="bg-gradient-to-br from-emerald-50 to-gray-50 rounded-2xl p-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">What is Sunya?</h2>
      <div className="prose max-w-none">
        <p className="text-gray-700 leading-relaxed mb-4 text-lg">
          Sunya, derived from the Sanskrit word "Sunyata" meaning "emptiness" or "potential," is a universal meditation practice accessible to all, regardless of religious or spiritual background. It is NOT a religion but a scientifically-grounded path to inner peace and transformation.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          Based on Maha Anapana Smrti from early Buddhist teachings, Sunya integrates three essential practices: body training, breath training, and mind training. The goal is not miraculous powers, but genuine peace and prosperity through consistent, effortless practice.
        </p>
        <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-lg mt-6">
          <p className="text-gray-800 font-semibold mb-2">Maha Anapana Smrti Explained:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li><strong>Maha</strong> = Great/Advanced</li>
            <li><strong>Anapana</strong> = Breathing in and out</li>
            <li><strong>Smrti</strong> = Spontaneous mental focus (Samatha Vipasyana)</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Three Areas of Training */}
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Three Areas of Training</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-emerald-100">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <span className="text-emerald-700 font-bold text-xl">1</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Body Training</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Hand swing exercises for circulation</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Rising and lowering hands for energy flow</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Various sitting positions (full lotus, half lotus, comfortable seated)</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span>Synchronizing body, breath, and mind</li>
          </ul>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-blue-100">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
            <span className="text-blue-700 font-bold text-xl">2</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Breath Training</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Natural breathing without force</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>No excessive effort required</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Integration with body movements</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Balanced, effortless breathing</li>
          </ul>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-purple-100">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
            <span className="text-purple-700 font-bold text-xl">3</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">Mind Training</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Observation without attachment</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Detachment from disturbances</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Achieving peace and clarity</li>
            <li className="flex items-start"><span className="text-purple-600 mr-2">•</span>Present-moment awareness</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Reverse Counting Technique */}
    <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">The Reverse Counting Technique</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <p className="text-gray-700 mb-4">
            The primary Sunya technique is elegantly simple yet profoundly effective:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">1</div>
              <span className="text-gray-700">Count backwards from <strong>99 to 0</strong> (or <strong>29 to 0</strong> for beginners)</span>
            </li>
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">2</div>
              <span className="text-gray-700">One count per breath cycle</span>
            </li>
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">3</div>
              <span className="text-gray-700">Takes 12-15 minutes for full session</span>
            </li>
            <li className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">4</div>
              <span className="text-gray-700">Focus on the counting, not the result</span>
            </li>
          </ul>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6">
          <h4 className="font-bold text-gray-900 mb-3">Why Reverse Counting?</h4>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Counting backwards requires more mental focus than counting forwards, naturally preventing the mind from wandering. This simple technique creates a powerful anchor for awareness, gradually leading to deeper states of tranquility and insight.
          </p>
          <h4 className="font-bold text-gray-900 mb-3">Two Levels of Practice:</h4>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span><strong>Basic Samatha Vipasyana (BSV):</strong> Harmonizing mind and body</li>
            <li className="flex items-start"><span className="text-emerald-600 mr-2">•</span><strong>Maha Samatha Vipasyana (MSV):</strong> Interaction with environment, performing duties with focus</li>
          </ul>
        </div>
      </div>
    </div>

    {/* When to Practice */}
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">When to Practice</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-green-600 text-2xl mr-2">✓</span> Best Times:
          </h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Any time is suitable for practice</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Morning for freshness</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>Evening for relaxation</li>
            <li className="flex items-start"><span className="text-blue-600 mr-2">•</span>During breaks for mental reset</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <span className="text-red-600 text-2xl mr-2">✗</span> Avoid:
          </h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Before or after shower</li>
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>Immediately after heavy meals</li>
            <li className="flex items-start"><span className="text-red-600 mr-2">•</span>After intense exercise</li>
            <li className="flex items-start"><span className="text-green-600 mr-2">•</span>Women can practice during menstruation ✓</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Core Principles */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200">
        <h4 className="text-lg font-bold text-gray-900 mb-4">Core Principles</h4>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start">
            <span className="text-teal-600 font-bold mr-2">•</span>
            <div>
              <strong>Samatha:</strong> Practice leading to tranquil, serene mind
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-teal-600 font-bold mr-2">•</span>
            <div>
              <strong>Vipasyana:</strong> Introspective observation and insight
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-teal-600 font-bold mr-2">•</span>
            <div>
              <strong>Effortless Practice:</strong> Like tuning a guitar - not too tight, not too loose
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-teal-600 font-bold mr-2">•</span>
            <div>
              <strong>Non-religious:</strong> Accessible to all faiths and backgrounds
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-teal-600 font-bold mr-2">•</span>
            <div>
              <strong>Goal:</strong> Peace and prosperity, not miraculous powers
            </div>
          </li>
        </ul>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
        <h4 className="text-lg font-bold text-gray-900 mb-4">Key Reminders</h4>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start">
            <span className="text-purple-600 font-bold mr-2">✓</span>
            <div>Sunya is <strong>NOT a religion</strong></div>
          </li>
          <li className="flex items-start">
            <span className="text-purple-600 font-bold mr-2">✓</span>
            <div>No harm to body or health</div>
          </li>
          <li className="flex items-start">
            <span className="text-purple-600 font-bold mr-2">✓</span>
            <div>Universal and inclusive practice</div>
          </li>
          <li className="flex items-start">
            <span className="text-purple-600 font-bold mr-2">✓</span>
            <div>Scientific approach to spirituality</div>
          </li>
          <li className="flex items-start">
            <span className="text-purple-600 font-bold mr-2">✓</span>
            <div>Integration with daily duties</div>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const SunyaBelts = () => (
  <div className="space-y-8">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">The 7-Belt Progressive System</h2>
      <p className="text-gray-600 max-w-2xl mx-auto">
        Sunya practice follows a structured progression through seven levels, each represented by a color belt (ROYGBIV). The ultimate goal is Arhant - one who can freely enter and leave this world.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {mockData.sunyaBelts.map((belt) => (
        <div
          key={belt.level}
          className="bg-white rounded-xl p-6 shadow-md border-2 hover:shadow-xl transition-all"
          style={{ borderColor: belt.color }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg"
              style={{ backgroundColor: belt.color }}
            >
              {belt.level}
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{belt.name}</h3>
              <span className="text-xs text-gray-500">Level {belt.level}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600">{belt.description}</p>
        </div>
      ))}
    </div>

    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">The Goal: Arhant</h3>
      <p className="text-gray-700 text-center max-w-2xl mx-auto">
        According to Buddha, the ultimate achievement is becoming an Arhant - one who has attained liberation and can freely enter and leave this world with complete mastery over existence.
      </p>
    </div>
  </div>
);

const SunyaRetreats = () => (
  <div className="space-y-8">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">Sunya Retreats & Training</h2>
      <p className="text-gray-600 max-w-2xl mx-auto">
        Experience transformative 3-day residential or non-residential programs at our retreat centers across India and Malaysia
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {mockData.retreatLocations.map((location, index) => (
        <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
          <div className="relative h-56 overflow-hidden">
            <img
              src={location.image}
              alt={location.name}
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
            />
          </div>
          <div className="p-6">
            <div className="flex items-center space-x-2 text-emerald-700 mb-2">
              <MapPin size={18} />
              <span className="text-sm font-semibold">{location.location}</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{location.name}</h3>
            <p className="text-gray-600 text-sm mb-4">{location.description}</p>
            <button className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium">
              Learn More
            </button>
          </div>
        </div>
      ))}
    </div>

    <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-8 text-white text-center">
      <h3 className="text-2xl font-bold mb-4">Ready to Begin Your Journey?</h3>
      <p className="mb-6 text-emerald-100">Join thousands of practitioners worldwide in experiencing the peace and transformation of Sunya meditation</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button className="px-8 py-3 bg-white text-emerald-700 rounded-full font-semibold hover:bg-gray-100 transition-all">
          Register for Retreat
        </button>
        <button className="px-8 py-3 bg-emerald-700 text-white rounded-full font-semibold hover:bg-emerald-800 transition-all border border-white/30">
          Start Online Training
        </button>
      </div>
    </div>
  </div>
);

const Sunya = () => {
  const { section } = useParams();

  const sections = [
    { id: 'what-is', name: 'What is Sunya?', component: SunyaWhatIs },
    { id: 'practice', name: 'Sunya Practice Guide', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Practice Guide - Coming Soon</h2></div> },
    { id: 'belts', name: 'The 7-Belt System', component: SunyaBelts },
    { id: 'ethics', name: 'Ethical Foundations', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Ethical Foundations - Coming Soon</h2></div> },
    { id: 'foundation', name: 'Sunyatee Foundation', component: SunyateeFoundation },
    { id: 'retreats', name: 'Retreats & Training', component: SunyaRetreats },
    { id: 'online', name: 'Online Training', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Online Training - Coming Soon</h2></div> },
    { id: 'welfare', name: 'Social Welfare', component: () => <div className="text-center py-20"><h2 className="text-2xl font-bold text-gray-900">Social Welfare - Coming Soon</h2></div> }
  ];

  const currentSection = section || 'what-is';
  const CurrentComponent = sections.find(s => s.id === currentSection)?.component || SunyaWhatIs;

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="Sunya Meditation & Sunyatee Foundation"
        description="Learn about Sunya meditation - a universal, non-religious practice for peace and transformation. Discover the 7-belt system, reverse counting technique, and join retreats worldwide."
        keywords="Sunya meditation, Sunyatee Foundation, Maha Anapana Smrti, reverse counting meditation, 7-belt system, meditation retreat, Dr. Lim Siow Jin teachings"
        
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Sunya & Sunyatee Foundation</h1>
              <p className="text-xl text-emerald-100">Universal meditation practice for peace and transformation</p>
            </div>
            <SocialShare 
              title="Sunya Meditation - Universal Path to Peace"
              description="Discover Sunya meditation - a non-religious practice for inner peace and transformation, taught by Dr. Lim Siow Jin."
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {sections.map((sec) => (
              <Link
                key={sec.id}
                to={`/sunya/${sec.id}`}
                className={`px-6 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  currentSection === sec.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-600 hover:text-emerald-700 hover:border-gray-300'
                }`}
              >
                {sec.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <CurrentComponent />
      </div>
    </div>
  );
};

export default Sunya;